import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  Brain,
  TrendingDown,
  Filter,
} from 'lucide-react';
import { DCAExecution } from '../../types/dca';

export function DCAHistory() {
  const [filter, setFilter] = useState<'all' | 'confirmed' | 'failed'>('all');

  // Mock executions - will be replaced with real data
  const executions: DCAExecution[] = [
    {
      id: '1',
      strategyId: '1',
      timestamp: Date.now() - 2 * 24 * 60 * 60 * 1000,
      txHash: '0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
      sourceAmount: '100',
      targetAmount: '0.0445',
      executionPrice: '2247.19',
      gasFee: '2.50',
      platformFee: '0.50',
      status: 'confirmed',
      wasSmartTimed: true,
      wasDipBuy: false,
      priceAtScheduledTime: '2263.45',
      savedAmount: '0.72',
    },
    {
      id: '2',
      strategyId: '1',
      timestamp: Date.now() - 9 * 24 * 60 * 60 * 1000,
      txHash: '0xabcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890',
      sourceAmount: '100',
      targetAmount: '0.0462',
      executionPrice: '2164.50',
      gasFee: '1.80',
      platformFee: '0.50',
      status: 'confirmed',
      wasSmartTimed: true,
      wasDipBuy: true,
      savedAmount: '3.45',
    },
    {
      id: '3',
      strategyId: '2',
      timestamp: Date.now() - 15 * 24 * 60 * 60 * 1000,
      txHash: '0x567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef1234',
      sourceAmount: '500',
      targetAmount: '0.01172',
      executionPrice: '42662.11',
      gasFee: '3.20',
      platformFee: '2.50',
      status: 'confirmed',
      wasSmartTimed: true,
      wasDipBuy: false,
      savedAmount: '8.50',
    },
    {
      id: '4',
      strategyId: '3',
      timestamp: Date.now() - 1 * 24 * 60 * 60 * 1000,
      txHash: '0xdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890abc',
      sourceAmount: '50',
      targetAmount: '0',
      executionPrice: '0',
      gasFee: '0',
      platformFee: '0',
      status: 'failed',
      wasSmartTimed: false,
      wasDipBuy: false,
      errorMessage: 'Insufficient liquidity in pool',
    },
    {
      id: '5',
      strategyId: '1',
      timestamp: Date.now() - 16 * 24 * 60 * 60 * 1000,
      txHash: '0x890abcdef1234567890abcdef1234567890abcdef1234567890abcdef12345678',
      sourceAmount: '100',
      targetAmount: '0.0438',
      executionPrice: '2283.10',
      gasFee: '2.10',
      platformFee: '0.50',
      status: 'confirmed',
      wasSmartTimed: false,
      wasDipBuy: false,
    },
  ];

  const filteredExecutions = executions.filter((exec) => {
    if (filter === 'all') return true;
    return exec.status === filter;
  });

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Confirmado</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">Fallido</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">Pendiente</Badge>;
      default:
        return null;
    }
  };

  const truncateHash = (hash: string) => {
    return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  };

  // Calculate total savings
  const totalSavings = executions
    .filter((e) => e.status === 'confirmed' && e.savedAmount)
    .reduce((sum, e) => sum + parseFloat(e.savedAmount || '0'), 0);

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Ahorro Total con Smart Timing</p>
              <p className="text-2xl font-bold text-green-500">${totalSavings.toFixed(2)}</p>
            </div>
            <Brain className="h-8 w-8 text-violet-500" />
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        <div className="flex gap-2">
          {(['all', 'confirmed', 'failed'] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'Todos' : f === 'confirmed' ? 'Confirmados' : 'Fallidos'}
            </Button>
          ))}
        </div>
      </div>

      {/* Executions List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historial de Ejecuciones</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredExecutions.length === 0 ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-400">
              No hay ejecuciones que mostrar
            </div>
          ) : (
            <div className="space-y-4">
              {filteredExecutions.map((execution) => (
                <div
                  key={execution.id}
                  className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(execution.status)}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          ${execution.sourceAmount} → {execution.targetAmount || '0'} tokens
                        </span>
                        {execution.wasSmartTimed && (
                          <Brain className="h-4 w-4 text-violet-500" aria-label="Smart Timed" />
                        )}
                        {execution.wasDipBuy && (
                          <TrendingDown className="h-4 w-4 text-green-500" aria-label="Dip Buy" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <span>{formatDate(execution.timestamp)}</span>
                        {execution.status === 'confirmed' && (
                          <>
                            <span>·</span>
                            <span>@ ${execution.executionPrice}</span>
                          </>
                        )}
                      </div>
                      {execution.status === 'failed' && execution.errorMessage && (
                        <p className="text-sm text-red-500 mt-1">{execution.errorMessage}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {execution.savedAmount && parseFloat(execution.savedAmount) > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-slate-600 dark:text-slate-400">Ahorro</p>
                        <p className="text-green-500 font-medium">+${execution.savedAmount}</p>
                      </div>
                    )}

                    {getStatusBadge(execution.status)}

                    {execution.status !== 'failed' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          window.open(`https://etherscan.io/tx/${execution.txHash}`, '_blank');
                        }}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
